import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, Trash2, ArrowUp, ArrowDown, FileText, MapPin, X, Truck, RotateCcw, History, Route as RouteIcon, GripVertical, Sparkles, Clock, Crown } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { useSubscription } from "@/lib/subscription";
import { Link } from "react-router-dom";

const CHOFERES = ["Chofer 1", "Chofer 2", "Chofer 3"];
const VEHICULOS = ["Vehículo 1", "Vehículo 2", "Vehículo 3"];

const getCurrentDateTime = () => {
  const now = new Date();
  const fecha = now.toISOString().split("T")[0];
  const hora = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return { fecha, hora };
};

/**
 * Extract (lat, lng) from a Google Maps URL **or** from a plain
 * "lat, lng" string pasted by the user. Mirrors the backend regex so the UI
 * can decide whether the "Optimizar" button is actionable without having to
 * round-trip an empty request to the server.
 */
const extractLatLng = (link) => {
  if (!link) return null;
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&](?:q|query|ll|destination)=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    // Plain "lat,lng" — accepts optional spaces around the comma.
    /^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/,
  ];
  for (const rx of patterns) {
    const m = link.match(rx);
    if (m) {
      const lat = parseFloat(m[1]);
      const lng = parseFloat(m[2]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) + Math.abs(lng) > 0.0001) {
        return { lat, lng };
      }
    }
  }
  return null;
};

const fmtMinutes = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
};

export default function RoutePlanning() {
  const { hasFeature } = useSubscription();
  const canOptimize = hasFeature("route_optimization");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProvincia, setFilterProvincia] = useState("__all__");
  const [filterCanton, setFilterCanton] = useState("__all__");
  const [route, setRoute] = useState([]);
  const [optimizing, setOptimizing] = useState(false);
  const [optimization, setOptimization] = useState(null); // {total_distance_km, total_minutes, return_distance_km, skipped_count};
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [deleteRouteId, setDeleteRouteId] = useState(null);
  const [routeMeta, setRouteMeta] = useState(() => {
    const { fecha, hora } = getCurrentDateTime();
    return {
      fecha,
      vendedor: CHOFERES[0],
      vehiculo: VEHICULOS[0],
      hora_salida: hora,
      observaciones: "",
    };
  });
  const [company, setCompany] = useState({ direccion_cedi: "", cedi_maps_link: "", cedi_lat: 0, cedi_lng: 0 });

  useEffect(() => {
    fetchCustomers();
    fetchSavedRoutes();
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const res = await api.get("/company");
      setCompany({
        direccion_cedi: res.data.direccion_cedi || "",
        cedi_maps_link: res.data.cedi_maps_link || "",
        cedi_lat: res.data.cedi_lat || 0,
        cedi_lng: res.data.cedi_lng || 0,
      });
      const horaDefault = res.data.hora_salida_default || "";
      if (horaDefault) {
        // Override the initial-state "current time" with the company's
        // configured default. Only on mount; user can still change it.
        setRouteMeta(prev => ({ ...prev, hora_salida: horaDefault }));
      }
    } catch { /* ignore */ }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedRoutes = async () => {
    try {
      const response = await api.get("/routes");
      setSavedRoutes(response.data);
    } catch { /* ignore */ }
  };

  const provincias = useMemo(
    () => [...new Set(customers.map(c => c.provincia).filter(Boolean))].sort(),
    [customers]
  );
  
  const cantonesFiltrados = useMemo(() => {
    const filtered = filterProvincia && filterProvincia !== "__all__"
      ? customers.filter(c => c.provincia === filterProvincia)
      : customers;
    return [...new Set(filtered.map(c => c.canton).filter(Boolean))].sort();
  }, [customers, filterProvincia]);

  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch = !term || 
        (c.nombre_facturacion || "").toLowerCase().includes(term) ||
        (c.nombre_fantasia || "").toLowerCase().includes(term) ||
        (c.codigo || "").toLowerCase().includes(term) ||
        (c.provincia || "").toLowerCase().includes(term) ||
        (c.canton || "").toLowerCase().includes(term) ||
        (c.distrito || "").toLowerCase().includes(term);
      const matchesProvincia = !filterProvincia || filterProvincia === "__all__" || c.provincia === filterProvincia;
      const matchesCanton = !filterCanton || filterCanton === "__all__" || c.canton === filterCanton;
      return matchesSearch && matchesProvincia && matchesCanton;
    });
  }, [customers, searchTerm, filterProvincia, filterCanton]);

  const addToRoute = (customer) => {
    if (route.some(s => s.customer_id === customer.id)) {
      toast.error(`${customer.nombre_facturacion} ya está en la ruta`);
      return;
    }
    setRoute([...route, {
      customer_id: customer.id,
      codigo: customer.codigo || "",
      nombre_facturacion: customer.nombre_facturacion,
      nombre_fantasia: customer.nombre_fantasia || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
      provincia: customer.provincia || "",
      canton: customer.canton || "",
      distrito: customer.distrito || "",
      notes: customer.notes || "",
      maps_link: customer.maps_link || "",
      lat: customer.lat || 0,
      lng: customer.lng || 0,
    }]);
    setOptimization(null);
    toast.success(`${customer.nombre_facturacion} agregado a la ruta`);
  };

  const removeFromRoute = (index) => {
    setRoute(route.filter((_, i) => i !== index));
    setOptimization(null);
  };

  const moveStop = (index, direction) => {
    const newRoute = [...route];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newRoute.length) return;
    [newRoute[index], newRoute[newIndex]] = [newRoute[newIndex], newRoute[index]];
    setRoute(newRoute);
    setOptimization(null);
  };

  // Drag & drop reorder
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const onDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(index)); } catch {}
  };
  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) setDragOverIndex(index);
  };
  const onDragLeave = () => setDragOverIndex(null);
  const onDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from === null || from === index) return;
    const next = [...route];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    setRoute(next);
    setOptimization(null);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const clearRoute = () => {
    setRoute([]);
    setOptimization(null);
    setClearConfirmOpen(false);
    toast.success("Ruta limpiada");
  };

  /**
   * Premium feature — POSTs the current stops + CEDI coords to
   * /api/routes/optimize. Replaces the route order with the server-computed
   * nearest-neighbour sequence and stores totals for the summary banner.
   * Stops without coordinates are appended at the end (server "skipped").
   */
  const optimizeRoute = async () => {
    if (!canOptimize) return;  // gated visually by the button itself
    if (route.length < 2) {
      toast.error("Necesitás al menos 2 paradas para optimizar");
      return;
    }
    // CEDI coordinates: explicit `cedi_lat`/`cedi_lng` saved in Mi Empresa
    // wins, then fall back to parsing `cedi_maps_link`. Short Google Maps
    // URLs (maps.app.goo.gl) don't embed coords — the user must capture
    // their location via the button in Mi Empresa for those.
    let cediCoords = null;
    if (company.cedi_lat && company.cedi_lng) {
      cediCoords = { lat: company.cedi_lat, lng: company.cedi_lng };
    } else {
      cediCoords = extractLatLng(company.cedi_maps_link || "");
    }
    if (!cediCoords) {
      toast.error(
        "Tu CEDI no tiene coordenadas. Andá a Mi Empresa → CEDI y tocá \"Usar mi ubicación actual\" estando en el local (o pegá un link largo de Maps con coordenadas).",
      );
      return;
    }
    setOptimizing(true);
    try {
      const { data } = await api.post("/routes/optimize", {
        stops: route,
        start_lat: cediCoords.lat,
        start_lng: cediCoords.lng,
        return_to_start: true,
        avg_speed_kmh: 30,
        service_min_per_stop: 10,
      });
      // Server returns geocoded stops + skipped (no coords). Render skipped
      // ones at the tail of the route so the driver still sees them.
      const merged = [...(data.stops || []), ...(data.skipped || [])];
      setRoute(merged);
      setOptimization({
        total_distance_km: data.total_distance_km || 0,
        total_minutes: data.total_minutes || 0,
        return_distance_km: data.return_distance_km || 0,
        optimized_count: (data.stops || []).length,
        skipped_count: (data.skipped || []).length,
        approx_count: (data.stops || []).filter(s => s.aproximada).length,
      });
      if ((data.skipped || []).length > 0) {
        toast.warning(`${(data.skipped || []).length} parada(s) sin coordenadas se dejaron al final`);
      } else {
        const apx = (data.stops || []).filter(s => s.aproximada).length;
        toast.success(
          `Ruta optimizada: ${data.total_distance_km} km · ${fmtMinutes(data.total_minutes)}` +
          (apx > 0 ? ` (${apx} aproximada${apx > 1 ? "s" : ""})` : ""),
        );
      }
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det?.error === "plan_required") {
        toast.error("Esta función requiere plan Premium o Pro");
      } else {
        toast.error(det || det?.message || "Error al optimizar ruta");
      }
    } finally {
      setOptimizing(false);
    }
  };

  const openInMaps = (stop) => {
    if (stop.maps_link) {
      window.open(stop.maps_link, "_blank");
      return;
    }
    const fullAddress = [stop.address, stop.distrito, stop.canton, stop.provincia, "Costa Rica"]
      .filter(Boolean).join(", ");
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
    window.open(url, "_blank");
  };

  const openFullRouteInMaps = () => {
    if (route.length === 0) {
      toast.error("No hay paradas en la ruta");
      return;
    }

    // IMPORTANT: Google Maps /maps/dir/?api=1 accepts these as waypoints:
    //   • "lat,lng" coordinate pairs (most reliable — always geocodes)
    //   • plain text addresses (Google geocodes, but fails on partial /
    //     vernacular Costa Rican addresses like "75m sur del parque…")
    //   • Place IDs (we don't have these)
    //
    // Short Maps URLs (maps.app.goo.gl/...) DO NOT WORK as waypoints.
    //
    // Strategy: prefer "lat,lng" for every stop that already has coords
    // (either from the customer record or filled by the optimizer's
    // geo_cr fallback). For stops with no coords at all, fall back to
    // an address-text waypoint as last resort.
    const waypointFor = (stop) => {
      if (stop.lat && stop.lng) {
        return `${stop.lat},${stop.lng}`;
      }
      // Try parsing the customer's own maps_link as a last resort before
      // falling back to text.
      const parsed = extractLatLng(stop.maps_link || "");
      if (parsed) return `${parsed.lat},${parsed.lng}`;
      const parts = [stop.address, stop.distrito, stop.canton, stop.provincia, "Costa Rica"]
        .filter(Boolean);
      return parts.join(", ") || stop.nombre_facturacion || "Costa Rica";
    };

    // CEDI origin/destination: prefer explicit cedi_lat/lng (set via "Usar
    // mi ubicación actual" in Mi Empresa), then long Maps link with
    // coords, then finally the human-readable address text.
    let cediWaypoint = "";
    if (company.cedi_lat && company.cedi_lng) {
      cediWaypoint = `${company.cedi_lat},${company.cedi_lng}`;
    } else {
      const parsed = extractLatLng(company.cedi_maps_link || "");
      if (parsed) cediWaypoint = `${parsed.lat},${parsed.lng}`;
      else cediWaypoint = (company.direccion_cedi || "").trim();
    }

    if (cediWaypoint) {
      const origin = encodeURIComponent(cediWaypoint);
      const destination = encodeURIComponent(cediWaypoint);
      const waypoints = route.map(s => encodeURIComponent(waypointFor(s))).join("|");
      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving&waypoints=${waypoints}`;
      window.open(url, "_blank");
      return;
    }

    // No CEDI at all → use first stop as origin and last as destination.
    if (route.length === 1) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(waypointFor(route[0]))}`;
      window.open(url, "_blank");
      return;
    }

    const origin = encodeURIComponent(waypointFor(route[0]));
    const destination = encodeURIComponent(waypointFor(route[route.length - 1]));
    const waypoints = route.slice(1, -1).map(s => encodeURIComponent(waypointFor(s))).join("|");

    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${waypoints}`;

    window.open(url, "_blank");
  };

  const loadFromHistory = (saved) => {
    setRoute(saved.stops || []);
    setRouteMeta({
      fecha: saved.fecha,
      vendedor: saved.vendedor || CHOFERES[0],
      vehiculo: saved.vehiculo || VEHICULOS[0],
      hora_salida: saved.hora_salida || "",
      observaciones: saved.observaciones || "",
    });
    setHistoryOpen(false);
    toast.success("Ruta cargada desde historial");
  };

  const deleteRouteFromHistory = async () => {
    if (!deleteRouteId) return;
    try {
      await api.delete(`/routes/${deleteRouteId}`);
      toast.success("Ruta eliminada del historial");
      setDeleteRouteId(null);
      fetchSavedRoutes();
    } catch {
      toast.error("Error al eliminar ruta");
    }
  };

  const generatePDF = async () => {
    if (route.length === 0) {
      toast.error("Agrega al menos un cliente a la ruta");
      return;
    }
    if (!routeMeta.fecha) {
      toast.error("La fecha de la ruta es requerida");
      return;
    }
    if (!routeMeta.vendedor.trim()) {
      toast.error("El nombre del responsable es requerido");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/routes/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...routeMeta,
          stops: route,
          cedi_address: company.direccion_cedi || "",
          cedi_maps_link: company.cedi_maps_link || "",
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Error al generar PDF");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ruta_${routeMeta.fecha}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF generado exitosamente");
      fetchSavedRoutes();
    } catch (error) {
      toast.error(error.message || "Error al generar PDF");
    }
  };

  const zonasCubiertas = useMemo(
    () => [...new Set(route.map(s => s.provincia).filter(Boolean))],
    [route]
  );

  if (loading) {
    return <PageLoader label="Cargando ruta…" testid="route-loader" />;
  }

  return (
    <div data-testid="route-planning-page">
      <div className="mb-6">
        <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Planificación de Rutas
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Selecciona clientes, ordénalos y genera un PDF con la ruta planificada
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Lista de clientes - 3 columnas */}
        <div className="lg:col-span-3">
          <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="customer-search-input"
                  placeholder="Buscar por nombre, negocio, código o ubicación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-sm pl-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={filterProvincia} onValueChange={(v) => { setFilterProvincia(v); setFilterCanton("__all__"); }}>
                  <SelectTrigger className="rounded-sm" data-testid="filter-provincia-select">
                    <SelectValue placeholder="Todas las provincias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas las provincias</SelectItem>
                    {provincias.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterCanton} onValueChange={setFilterCanton}>
                  <SelectTrigger className="rounded-sm" data-testid="filter-canton-select">
                    <SelectValue placeholder="Todos los cantones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los cantones</SelectItem>
                    {cantonesFiltrados.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Clientes Disponibles</h3>
              <span className="text-xs text-slate-500">{filteredCustomers.length} resultados</span>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredCustomers.length === 0 ? (
                <p className="text-center py-12 text-slate-500 text-sm">No se encontraron clientes</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCustomers.map(c => {
                    const inRoute = route.some(s => s.customer_id === c.id);
                    return (
                      <div key={c.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid="available-customer-row">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2 mb-1">
                              {c.codigo && (
                                <span className="text-xs mono px-1.5 py-0.5 bg-slate-100 rounded-sm font-bold text-slate-700 flex-shrink-0">
                                  {c.codigo}
                                </span>
                              )}
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate">{c.nombre_facturacion}</p>
                                {c.nombre_fantasia && (
                                  <p className="text-xs text-slate-500 truncate">{c.nombre_fantasia}</p>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-slate-600">
                              {c.phone && <div>📞 {c.phone}</div>}
                              {c.email && <div className="truncate">✉ {c.email}</div>}
                              {(c.provincia || c.canton) && (
                                <div className="col-span-2 truncate">
                                  📍 {[c.provincia, c.canton, c.distrito].filter(Boolean).join(", ")}
                                </div>
                              )}
                              {c.address && <div className="col-span-2 truncate text-slate-500">{c.address}</div>}
                              {c.notes && <div className="col-span-2 text-xs italic text-slate-500 truncate">{c.notes}</div>}
                            </div>
                          </div>
                          <Button
                            onClick={() => addToRoute(c)}
                            disabled={inRoute}
                            size="sm"
                            className={inRoute ? "rounded-sm bg-slate-200 text-slate-500" : "btn-primary rounded-sm"}
                            data-testid={`add-to-route-${c.id}`}
                          >
                            {inRoute ? "Agregado" : <><Plus className="h-3 w-3 mr-1" /> Agregar</>}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Carrito de ruta - 2 columnas */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-[rgb(0,47,167)] text-white flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Ruta Planificada
              </h3>
              <div className="flex items-center gap-2">
                {canOptimize ? (
                  <Button
                    onClick={optimizeRoute}
                    disabled={optimizing || route.length < 2}
                    size="sm"
                    className="h-7 rounded-sm bg-white text-[rgb(0,47,167)] hover:bg-blue-50 border-0 font-bold text-xs px-2"
                    data-testid="optimize-route-button"
                    title="Reordenar paradas por cercanía (vecino más cercano)"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {optimizing ? "Optimizando…" : "Optimizar"}
                  </Button>
                ) : (
                  <Link
                    to="/upgrade?reason=route_optimization"
                    className="inline-flex items-center gap-1 h-7 rounded-sm bg-amber-400/20 text-amber-50 border border-amber-300/40 hover:bg-amber-400/30 font-bold text-[11px] px-2"
                    data-testid="optimize-route-locked"
                    title="Disponible en Premium o Pro"
                  >
                    <Crown className="h-3 w-3" />
                    Optimizar
                  </Link>
                )}
                <span className="text-xs bg-white text-[rgb(0,47,167)] px-2 py-0.5 rounded-sm font-bold">
                  {route.length} {route.length === 1 ? "parada" : "paradas"}
                </span>
              </div>
            </div>

            {optimization && (
              <div className="px-3 py-2.5 bg-emerald-50 border-b border-emerald-200 text-xs grid grid-cols-3 gap-2" data-testid="optimization-summary">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Distancia total</div>
                  <div className="text-sm font-black text-emerald-900 mono" data-testid="optimization-total-km">
                    {optimization.total_distance_km.toFixed(2)} km
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Tiempo total</div>
                  <div className="text-sm font-black text-emerald-900 mono" data-testid="optimization-total-min">
                    {fmtMinutes(optimization.total_minutes)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Vuelta al CEDI</div>
                  <div className="text-sm font-black text-emerald-900 mono">
                    {optimization.return_distance_km.toFixed(2)} km
                  </div>
                </div>
                {optimization.skipped_count > 0 && (
                  <div className="col-span-3 text-[11px] text-amber-700 italic">
                    ⚠ {optimization.skipped_count} parada(s) sin coordenadas ni distrito — quedaron al final.
                  </div>
                )}
                {optimization.approx_count > 0 && (
                  <div className="col-span-3 text-[11px] text-amber-800 italic" data-testid="optimization-approx-note">
                    {optimization.approx_count} parada(s) usan ubicación <strong>aproximada</strong> por provincia/cantón/distrito (sin link de Maps). El chofer debe pedir referencia.
                  </div>
                )}
              </div>
            )}

            <div className="max-h-[400px] overflow-y-auto">
              {route.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Truck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500">No hay clientes en la ruta</p>
                  <p className="text-xs text-slate-400 mt-1">Agrega clientes desde la lista de la izquierda</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {route.map((stop, index) => (
                    <div
                      key={stop.customer_id}
                      className={`p-3 transition-colors ${dragOverIndex === index ? "bg-blue-50 border-l-2 border-l-[rgb(0,47,167)]" : ""} ${dragIndex === index ? "opacity-50" : ""}`}
                      draggable
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver(index)}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop(index)}
                      onDragEnd={onDragEnd}
                      data-testid={`route-stop-${index}`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className="flex-shrink-0 flex items-center justify-center cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 self-stretch"
                          title="Arrastrar para reordenar"
                          data-testid={`route-stop-drag-handle-${index}`}
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                        <div className="flex-shrink-0 w-7 h-7 bg-[rgb(0,47,167)] text-white rounded-full flex items-center justify-center font-bold text-xs">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{stop.nombre_facturacion}</p>
                          {stop.nombre_fantasia && (
                            <p className="text-xs text-slate-500 truncate">{stop.nombre_fantasia}</p>
                          )}
                          {(stop.eta_minutes > 0 || stop.distance_km > 0) && (
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-testid={`route-stop-eta-${index}`}>
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <Clock className="h-2.5 w-2.5" />
                                ETA {fmtMinutes(stop.eta_minutes)}
                              </span>
                              {stop.distance_km > 0 && (
                                <span className="text-[10px] font-bold text-slate-500 mono">
                                  · {stop.distance_km.toFixed(2)} km
                                </span>
                              )}
                              {stop.aproximada && (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200"
                                  title={`Ubicación aproximada (nivel ${stop.aprox_granularidad || "?"}). Pedile referencia al cliente.`}
                                  data-testid={`route-stop-approx-${index}`}
                                >
                                  <MapPin className="h-2.5 w-2.5" />
                                  aprox · {stop.aprox_granularidad || "—"}
                                </span>
                              )}
                              {!stop.lat && !stop.lng && optimization && (
                                <span className="text-[10px] font-bold text-amber-700 italic">sin coords</span>
                              )}
                            </div>
                          )}
                          <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                            {stop.phone && <div>📞 {stop.phone}</div>}
                            {(stop.address || stop.canton) && (
                              <div className="truncate">📍 {[stop.address, stop.canton, stop.provincia].filter(Boolean).join(", ")}</div>
                            )}
                            {stop.notes && <div className="italic text-slate-500 truncate">{stop.notes}</div>}
                          </div>
                          <div className="flex gap-1 mt-2">
                            <Button
                              onClick={() => moveStop(index, -1)}
                              disabled={index === 0}
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 rounded-sm"
                              data-testid={`move-up-${index}`}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              onClick={() => moveStop(index, 1)}
                              disabled={index === route.length - 1}
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 rounded-sm"
                              data-testid={`move-down-${index}`}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              onClick={() => openInMaps(stop)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 rounded-sm text-xs"
                              data-testid={`view-maps-${index}`}
                            >
                              <MapPin className="h-3 w-3 mr-1" />
                              Maps
                            </Button>
                            <Button
                              onClick={() => removeFromRoute(index)}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 rounded-sm text-red-600 hover:bg-red-50 ml-auto"
                              data-testid={`remove-stop-${index}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {route.length > 0 && (
              <div className="p-3 border-t border-slate-200 bg-slate-50">
                <Button
                  onClick={() => setClearConfirmOpen(true)}
                  variant="outline"
                  className="w-full rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="clear-route-button"
                >
                  <RotateCcw className="h-3 w-3 mr-2" />
                  Limpiar Ruta
                </Button>
              </div>
            )}
          </Card>

          {/* Datos de la ruta */}
          <Card className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-semibold mb-3">Datos de la Ruta</h3>

            {/* CEDI banner */}
            {company.direccion_cedi ? (
              <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-sm text-xs" data-testid="cedi-banner">
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-[rgb(0,47,167)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold text-[rgb(0,47,167)]">Inicio y fin en CEDI</div>
                    <div className="text-slate-600 mt-0.5 line-clamp-2">{company.direccion_cedi}</div>
                    {!(company.cedi_lat && company.cedi_lng) && !extractLatLng(company.cedi_maps_link || "") && (
                      <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="cedi-no-coords-warning">
                        ⚠ El CEDI no tiene coordenadas. <Link to="/company" className="underline font-semibold">Configurálas en Mi Empresa</Link> para usar "Optimizar".
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-sm text-xs" data-testid="cedi-missing-banner">
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-amber-900">
                    Configura la dirección del CEDI en <strong>Mi Empresa</strong> para que las rutas inicien y terminen ahí.
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-600">Fecha *</label>
                <Input
                  type="date"
                  value={routeMeta.fecha}
                  onChange={(e) => setRouteMeta({ ...routeMeta, fecha: e.target.value })}
                  className="rounded-sm"
                  data-testid="route-date-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-600">Vendedor / Repartidor *</label>
                <Select value={routeMeta.vendedor} onValueChange={(v) => setRouteMeta({ ...routeMeta, vendedor: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="route-driver-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHOFERES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-600">Vehículo</label>
                  <Select value={routeMeta.vehiculo} onValueChange={(v) => setRouteMeta({ ...routeMeta, vehiculo: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="route-vehicle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICULOS.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-600">Hora Salida</label>
                  <Input
                    type="time"
                    value={routeMeta.hora_salida}
                    onChange={(e) => setRouteMeta({ ...routeMeta, hora_salida: e.target.value })}
                    className="rounded-sm"
                    data-testid="route-time-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-600">Observaciones</label>
                <Textarea
                  value={routeMeta.observaciones}
                  onChange={(e) => setRouteMeta({ ...routeMeta, observaciones: e.target.value })}
                  className="rounded-sm text-sm"
                  rows={2}
                  placeholder="Notas generales de la ruta..."
                  data-testid="route-notes-input"
                />
              </div>
            </div>
          </Card>

          {/* Resumen */}
          {route.length > 0 && (
            <Card className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Resumen</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total clientes:</span>
                  <span className="font-bold">{route.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Provincias:</span>
                  <span className="font-bold">{zonasCubiertas.length}</span>
                </div>
                {zonasCubiertas.length > 0 && (
                  <div className="text-xs text-slate-500 italic">{zonasCubiertas.join(", ")}</div>
                )}
              </div>
            </Card>
          )}

          <Button
            onClick={generatePDF}
            disabled={route.length === 0}
            className="w-full btn-primary rounded-sm h-12 text-base"
            data-testid="generate-pdf-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            Generar PDF de Ruta
          </Button>

          {route.length > 0 && (
            <Button
              onClick={openFullRouteInMaps}
              variant="outline"
              className="w-full rounded-sm h-10 border-[rgb(0,47,167)] text-[rgb(0,47,167)] hover:bg-[rgb(0,47,167)]/5"
              data-testid="open-route-maps-button"
            >
              <RouteIcon className="h-4 w-4 mr-2" />
              Ver Ruta Completa en Google Maps
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Limpiar toda la ruta?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán los {route.length} clientes de la ruta planificada. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearRoute}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-clear-route-button"
            >
              Limpiar Ruta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de Rutas</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {savedRoutes.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No hay rutas guardadas</p>
                <p className="text-xs text-slate-400 mt-1">Las rutas se guardan automáticamente al generar el PDF</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {savedRoutes.map(r => (
                  <Card key={r.id} className="bg-white border border-slate-200 rounded-sm p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-sm">{r.fecha}</span>
                          {r.hora_salida && (
                            <span className="text-xs text-slate-500">· {r.hora_salida}</span>
                          )}
                          <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded-sm font-medium text-slate-700">
                            {r.stops.length} {r.stops.length === 1 ? "parada" : "paradas"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600">
                          <strong>{r.vendedor}</strong>
                          {r.vehiculo && ` · ${r.vehiculo}`}
                        </p>
                        {r.observaciones && (
                          <p className="text-xs text-slate-500 italic mt-1 truncate">{r.observaciones}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          onClick={() => loadFromHistory(r)}
                          variant="outline"
                          size="sm"
                          className="rounded-sm"
                          data-testid={`load-route-${r.id}`}
                        >
                          Cargar
                        </Button>
                        <Button
                          onClick={() => setDeleteRouteId(r.id)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          data-testid={`delete-route-${r.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-100">
                      {r.stops.slice(0, 6).map((s, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-slate-50 rounded-sm text-slate-600">
                          {i + 1}. {s.nombre_facturacion}
                        </span>
                      ))}
                      {r.stops.length > 6 && (
                        <span className="text-xs text-slate-500">+{r.stops.length - 6} más</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Creada: {new Date(r.created_at).toLocaleString("es-ES")}
                    </p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRouteId} onOpenChange={(o) => !o && setDeleteRouteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar ruta del historial?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRouteFromHistory}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
